import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { getLocale, useIntl } from 'umi';
import { PagingQuery, Segment } from 'tea-component';
import { useDeepCompareEffect } from 'ahooks';
import moment from 'moment';
import useFilterExtend from '@intelligence/hooks/useFilterExtend';
import useFilterTagsOptions from '@intelligence/hooks/useFilterTagsOptions';
import CommonFilter from '@intelligence/components/CommonFilter';
import Summary from '@/pages/intelligence/components/SummaryCard';
import Tables from '@intelligence/pages/TopCharts/PCGames/components/GamesDetails/OverlapSteam/components/Tables';
import { buriedPointGame } from '@home/utils/buriedPoint';
import { dbiPageLogMes as PAGE_LOG_CFGS } from '@intelligence/utils/buriedPointCfg';
import {
  DEFAULTFILTERVALUE as defaultFilterValue,
  FILTERKEYLIST as filterKeyList,
  DEFAULTINPUTLIST as defaultInputList,
  SEGMENT_METRICS_MAP,
  SEGMENT_METRIC_FILTER_MAP,
  SEGMENT_FILTER_BUTTON_TEXT,
} from '@intelligence/pages/TopCharts/PCGames/components/GamesDetails/OverlapSteam/constants';
import type { IBaseProps, IGameType } from '@intelligence/types/common';
import type { SortByItemType } from '@intelligence/components/TableComponents/components/SortBy';
import {
  fetchAmpereOverlapGameFilters,
  fetchAmpereOverlapGamesRanks,
  fetchOverlapSteamGamesRanks,
  getSteamOverlapGameSummaryDatas,
  getSteamOverlapRegionCountryLevelConfig,
} from '@intelligence/services/overlapAnalysis';
import {
  SUMMARY_METRIC_LIST,
  OVERLAP_STEAM_SUMMARY_METRIC,
  OVERLAP_STEAM_ORDERMETRIC,
  OVERLAP_STEAM_METRICPARAMS,
} from '@intelligence/pages/TopCharts/PCGames/components/GamesDetails/OverlapSteam/constants/metrics';
import {
  currentValidEngagementLevel,
  formatPercentNumX,
  getCountryOptionConfig,
  getCurrentValidMarket,
  getLevelMap,
  TPlayedWishlistedLevelMap,
} from '@intelligence/pages/TopCharts/PCGames/components/GamesDetails/OverlapSteam/utils';
import { SortType } from '@intelligence/pages/TopCharts/_config/enums';
import { handleOverlapSteamGameDetailFilterParamsToApi as handleFilterToApiParams } from '@/pages/intelligence/pages/TopCharts/_config/topchartFilterCfg';
import { MarketSpecailType } from '@intelligence/hooks/useMarketSelector';
import useMarketFilterApi from '@/pages/intelligence/hooks/useMarketSelector';
import * as filterKeyMap from '@/pages/intelligence/components/Filter/FilterBase/_config';
import { RegionType } from '@intelligence/components/RegionCountrySelector/components/RegionTypeSelector';
import ExportTable from '@/pages/intelligence/components/ExportTable';
// import { getURLSearchParams, updateURLSearchParams } from '@intelligence/utils/commonUtils';
import './index.scss';
import { pcConsoleDetailOverlap } from '@intelligence/v3/constants/downloadLogPoint';
import { getDataSourceIcon } from '@intelligence/utils/getDataSourceImg';
import { getMetricsTablePlatformsCascaderOptions } from '@intelligence/pages/TopCharts/PCGames/_config/utils';
import { createPlatformCascaderDisplayRenderConfig } from '@intelligence/_config/components/filters/platformCascaderDisplayRender';

// eslint-disable-next-line @typescript-eslint/naming-convention
type OVERLAP_PAGE_TYPE = 'played' | 'wishlisted';
type OVERLAP_SEGMENT_TYPE = 'played_play' | 'played_wishlist' | 'wishlisted_play' | 'wishlisted_wishlist';
type OverlapSourceType = 'steam' | 'otherVendor';
type RegionCountryLevelConfigItem = { page_name: string };

const OTHER_VENDOR_ACTIVE_USERS_RANGE_KEY = 'overlapping_active_users_range';

const getAmperePlatformParams = (value: Record<string, string[]> = {}) => ({
  storefronts: Object.keys(value || {}),
  platforms: Object.values(value || {}).reduce<string[]>((result, items) => (
    result.concat(items || [])
  ), []),
});

const getSegmentRevenueGrowthDefault = (segmentKey: OVERLAP_SEGMENT_TYPE) => {
  const [metricFilter] = SEGMENT_METRIC_FILTER_MAP[segmentKey] || [];
  return {
    opt: metricFilter?.opt || '>',
    value: metricFilter?.value ?? 20,
  };
};

interface OverlapSteamDetailsOverlapProps extends IBaseProps {
  loading: boolean;
  entityType: IGameType;
  gameId: string;
  appOverlapSteamId: string;
  basicInfo: any;
}

const OverlapSteam: React.FC<OverlapSteamDetailsOverlapProps> = (props) => {
  const {
    loading,
    entityType = 'pc',
    gameId,
    appOverlapSteamId = '',
    basicInfo,
    overlapAppIdsLoading,
  } = props;

  const locale = getLocale();
  const { formatMessage } = useIntl();
  const [overlapSource, setOverlapSource] = useState<OverlapSourceType>('steam');

  const overlapSourceOptions = [
    {
      text: (
        <span className="overlap-source-segment-label">
          <img src={getDataSourceIcon('steam')} alt="" />
          <span>Steam</span>
        </span>
      ),
      value: 'steam',
    },
    {
      text: (
        <span className="overlap-source-segment-label">
          <img
            src={getDataSourceIcon(overlapSource === 'otherVendor' ? 'amperewhite' : 'otherVendor')}
            alt=""
          />
          <span>{formatMessage({ id: 'dbi_other_vendor' })}</span>
        </span>
      ),
      value: 'otherVendor',
    },
  ];

  const {
    name,
    // 游戏是否已上线
    is_release: isRelease,
  } = basicInfo || {};

  const PAGE_LOG_KEY =
    entityType === 'pc' ? 'pcGameDetailsOverlap' : `pc&consoleGameDetailsOverlap`;
  const PAGE_LOG_INFO = PAGE_LOG_CFGS?.[PAGE_LOG_KEY];

  // 在筛选器这里拿到缓存的值，并渲染
  const [tableLoading, setTableLoading] = useState(true);
  // 宽表总数据条数
  const [tableTotal, setTableTotal] = useState(0);
  // 宽表数据
  const [tableData, setTableData] = useState([]);
  const [otherVendorTableData, setOtherVendorTableData] = useState<any[]>([]);
  const [otherVendorTableTotal, setOtherVendorTableTotal] = useState(0);
  const [otherVendorTableLoading, setOtherVendorTableLoading] = useState(false);
  const [ampereFilterData, setAmpereFilterData] = useState<any>({
    platform: [],
    regions: [],
    markets: [],
  });
  const [summaryData, setSummaryData] = useState([]);
  const { detailFilterExtend, setFilterExtend } = useFilterExtend({});
  const { steamTagOptions } = useFilterTagsOptions();

  const [marketsListMap, setMarketListMap] = useState<{ played: []; wishlisted: [] }>();
  const marketsListMapRef = useRef(marketsListMap);
  const ampereDateInitializedRef = useRef(false);
  const ampereRequestIdRef = useRef(0);

  const { regionOptions } = useMarketFilterApi({
    filterType: MarketSpecailType.metricTable,
  });

  const engagementLevelExtend = detailFilterExtend?.engageLevel_segement_two;
  const steamTagExtend = detailFilterExtend?.steam_tag;
  const genreExtend = detailFilterExtend?.genretor;
  let defaultFilterValueTemp = defaultFilterValue;
  if (engagementLevelExtend) {
    defaultFilterValueTemp = {
      ...defaultFilterValueTemp,
      [filterKeyMap.ENGAGELEVEL_SEGEMENT_TWO]: {
        value: {
          ...engagementLevelExtend.value,
        },
      },
    };
  }
  // 继承 Steam Tags
  if (steamTagExtend) {
    defaultFilterValueTemp = {
      ...defaultFilterValueTemp,
      [filterKeyMap.STEAM_TAGS]: {
        value: steamTagExtend.value || [],
        logic: steamTagExtend.logic || 'AND',
      },
    };
  }
  // 继承 IEGG Genre
  if (genreExtend) {
    defaultFilterValueTemp = {
      ...defaultFilterValueTemp,
      [filterKeyMap.GENRETOR]: {
        source: genreExtend.source || 'iegg',
        mainGenres: genreExtend.mainGenres || [],
        subGenres: genreExtend.subGenres || [],
      },
    };
  }

  const defaultSegment: OVERLAP_SEGMENT_TYPE = isRelease ? 'played_play' : 'wishlisted_play';
  defaultFilterValueTemp = {
    ...defaultFilterValueTemp,
    [filterKeyMap.OVERLAPSTEAM_PLAYERS]: {
      value: defaultSegment,
    },
    [filterKeyMap.REVENUE_GROWTH]: {
      value: getSegmentRevenueGrowthDefault(defaultSegment),
    },
    [filterKeyMap.RANGE_TIME]: {
      type: 'month',
      value: [
        moment().subtract(11, 'months').startOf('month'),
        moment().endOf('month'),
      ],
    },
    [filterKeyMap.PLATFORMCASCADER]: {
      value: {},
    },
    [OTHER_VENDOR_ACTIVE_USERS_RANGE_KEY]: {
      value: { min: '', max: '' },
    },
  };

  /** 筛选器 value 数据 **/
  const [filterValue, setFilterValue] = useState<any>(defaultFilterValueTemp);
  const engagementLevelExtendValue = engagementLevelExtend?.value;

  const [fetchParams, setFetchParams] = useState(() => {
    const baseFetchParams = {
      order_metric: 'p&p overlap affinity',
      order: 'desc',
      page_name: isRelease ? 'played' : 'wishlisted',
      page: 1,
      page_size: 50,
      region_type: 'region',
      region: 'global',
      id: '',
    };

    // 初始化参数统一走 TopCharts 既有转换逻辑，避免本地重复拼装 steam_tag/steam_tag_relation/genre
    return handleFilterToApiParams(baseFetchParams, defaultFilterValueTemp);
  });
  const [otherVendorTableParams, setOtherVendorTableParams] = useState({
    page: 1,
    page_size: 50,
    order: 'desc',
    order_metric: 'active_users',
  });

  // 当前是否修改国家地区筛选器
  const isChangeRegionCountrySelectorRef = useRef<boolean>();

  const hasExtendValue =
    engagementLevelExtendValue?.engagementLevelA || engagementLevelExtendValue?.engagementLevelB;

  const hasUserChangedEngagementRef = useRef<boolean>(hasExtendValue);
  // Boolean(getURLSearchParams('custom_engagement'))
  const hasUserChangedEngagementTwoRef = useRef<boolean>(hasExtendValue);
  // Boolean(getURLSearchParams('custom_engagement_two')),

  const [summaryLoading, setSummaryLoading] = useState(true);
  const [metricsValue, setMetricsValue] = useState<string>(isRelease ? 'played_play' : 'wishlisted_play');

  const regionsLevelMapRef = useRef<TPlayedWishlistedLevelMap>();
  const marketsLevelMapRef = useRef<TPlayedWishlistedLevelMap>();

  const marketsOptions = useMemo(() => {
    const optionsConfig = getCountryOptionConfig(
      marketsListMap?.[fetchParams.page_name as 'played' | 'wishlisted'],
      regionOptions,
    );

    return optionsConfig;
  }, [marketsListMap, regionOptions, fetchParams.page_name]);

  // 根据有数据的国家选项更新筛选器国家选项配置，动态设置 REVENUE_GROWTH buttonText
  const currentDefaultInputList: any = useMemo(() => {
    const list = defaultInputList.map((item: any) => {
      if (item.key === filterKeyMap.REGION_COUNTRY_SELECTOR) {
        return { ...item, countryOptions: marketsOptions };
      }
      if (item.key === filterKeyMap.REVENUE_GROWTH) {
        return {
          ...item,
          buttonText: SEGMENT_FILTER_BUTTON_TEXT[metricsValue] || 'dbi_overlapsteam_played_play_percent',
          placeholderText: 'dbi_overlapsteam_filter_placeholder',
        };
      }
      return item;
    });
    return list;
  }, [marketsOptions, metricsValue]);

  const otherVendorPlatformOptions = useMemo(() => {
    const options = getMetricsTablePlatformsCascaderOptions(
      { [filterKeyMap.CASCADER]: { npd: [] } },
      createPlatformCascaderDisplayRenderConfig({ fullCheckGroupCount: 3, width: 220 }),
    );
    const platformSource = (options.children || []).find((item: any) => item?.key === 'npd');
    const platformApiOptions = ampereFilterData?.platform || [];
    const templatePlatforms = platformSource?.children || [];
    const sourcePlatforms = platformApiOptions.length > 0
      ? platformApiOptions
      : templatePlatforms.filter((platform: any) => ['PC', 'PlayStation', 'Xbox'].includes(platform?.label));
    const platformChildren = sourcePlatforms.map((platform: any) => {
      const templatePlatform = templatePlatforms.find((item: any) => item?.label === platform?.label)
        || templatePlatforms[0]
        || {};
      const platformNode = {
        ...templatePlatform,
        label: platform.label,
        value: platform.value,
        key: platform.value,
        parent: undefined,
        cascaderDataSource: undefined,
        cascaderDataSourceLabel: platform.label,
        cascaderDataSourceLabelKey: undefined,
        cascaderDataSourceValue: undefined,
        children: [],
      };
      const templateDevice = templatePlatform?.children?.[0] || {};
      platformNode.children = (platform.children || []).map((device: any) => {
        const deviceNode = {
          ...templateDevice,
          label: device.label,
          value: device.value,
          key: device.value,
          parent: platformNode,
          cascaderDataSource: platformNode,
          cascaderDataSourceLabel: platformNode.label,
          cascaderDataSourceLabelKey: undefined,
          cascaderDataSourceValue: platformNode.value,
        };
        return deviceNode;
      });
      return platformNode;
    });

    return {
      ...options,
      isUseClose: true,
      restoreAllWhenEmptyOnClose: true,
      hasThirdLevel: false,
      hasShortcut: false,
      mainCategory: 'dbi_platform',
      subCategory: 'Detailed Platform',
      children: platformChildren,
      shortcutOptions: [],
    };
  }, [ampereFilterData]);

  const otherVendorMarketsOptions = useMemo(() => getCountryOptionConfig(
    (ampereFilterData?.markets || []).map((market: string) => ({ market })),
    regionOptions,
  ), [ampereFilterData, regionOptions]);

  const otherVendorDefaultInputList = useMemo(() => {
    const secondRowIndex = defaultInputList.findIndex((item: any, index: number) => (
      index > 0 && item.key === filterKeyMap.ROW
    ));
    const firstRowInputList = defaultInputList.slice(0, secondRowIndex);

    const otherVendorFirstRow = firstRowInputList
      .filter((item: any) => item.key !== filterKeyMap.OVERLAPSTEAM_PLAYERS)
      .map((item: any) => (
        item.key === filterKeyMap.TIPICON
          ? { ...item, content: 'dbi_overlap_other_vendor_data_description' }
          : item
      ));
    const leftIndex = otherVendorFirstRow.findIndex((item: any) => item.key === filterKeyMap.LEFT);

    otherVendorFirstRow.splice(leftIndex + 1, 0,
      {
        savePopupVisble: 0,
        key: filterKeyMap.RANGE_TIME,
        calendarType: 'month',
        typeList: ['month'],
        isShowAlloftime: false,
        showSelectType: false,
        rangeTimeOtherStyle: false,
      },
      {
        savePopupVisble: 0,
        key: filterKeyMap.PLATFORMCASCADER,
        renderKey: (otherVendorPlatformOptions?.children || [])
          .map((platform: any) => `${platform.value}:${(platform.children || [])
            .map((device: any) => device.value).join(',')}`)
          .join('|'),
        type: 'cascader',
        options: otherVendorPlatformOptions,
        visible: 1,
        visibleinlist: 1,
      },
    );

    const findFilter = (key: string) => (
      currentDefaultInputList.find((item: any) => item.key === key)
    );
    const steamTagsFilter = findFilter(filterKeyMap.STEAM_TAGS);
    const genreFilter = findFilter(filterKeyMap.GENRETOR);
    const countryFilter = {
      ...findFilter(filterKeyMap.REGION_COUNTRY_SELECTOR),
      countryOptions: otherVendorMarketsOptions,
    };

    return [
      ...otherVendorFirstRow,
      { savePopupVisble: 0, key: filterKeyMap.ROW },
      { savePopupVisble: 0, key: filterKeyMap.LEFT },
      {
        savePopupVisble: 1,
        key: OTHER_VENDOR_ACTIVE_USERS_RANGE_KEY,
        type: filterKeyMap.PCU_RANGE,
        title: 'dbi_overlap_other_vendor_active_users_range',
        unitText: '-',
        leftPlaceholder: 'dbi_minimum',
        rightPlaceholder: 'dbi_maximum',
        width: 340,
      },
      { ...steamTagsFilter, style: { width: 300 } },
      genreFilter,
      countryFilter,
      { savePopupVisble: 0, key: filterKeyMap.RIGHT },
      { savePopupVisble: 0, key: filterKeyMap.DOWNLOAD },
    ].filter(Boolean);
  }, [currentDefaultInputList, otherVendorMarketsOptions, otherVendorPlatformOptions]);

  const filterCfg = {
    filterKeyList: [
      ...filterKeyList,
      filterKeyMap.RANGE_TIME,
      filterKeyMap.PLATFORMCASCADER,
      OTHER_VENDOR_ACTIVE_USERS_RANGE_KEY,
    ],
    regionFilterParams: {},
    defaultValue: defaultFilterValueTemp,
    resetFilterValue: {
      ...defaultFilterValueTemp,
    },
    showConfirmButton: false,
    isCustomLayoutFilter: true,
    specialParams: {
      defalutMarket: ['global'], // 没取到交集用的默认值
      disableNoChina: true,
      entityType,
      showSelectType: false,
      rangeTimeOtherStyle: false,
      steamTagOptions,
    },
  };

  useDeepCompareEffect(() => {
    const currentDefaultSegment: OVERLAP_SEGMENT_TYPE = isRelease ? 'played_play' : 'wishlisted_play';
    setFilterValue((prev: Record<string, any>) => ({
      ...prev,
      [filterKeyMap.OVERLAPSTEAM_PLAYERS]: {
        value: currentDefaultSegment,
      },
      [filterKeyMap.REVENUE_GROWTH]: {
        value: getSegmentRevenueGrowthDefault(currentDefaultSegment),
      },
    }));
    setMetricsValue(currentDefaultSegment);
  }, [isRelease]);

  const getGameData = async (fetchParams: any, isSearch = false) => {
    setTableLoading(true);
    const params = {
      ...fetchParams,
      page_size: isSearch
        ? tableTotal || fetchParams.page_size // 参数不能为 0
        : fetchParams.page_size,
      page: isSearch ? 1 : fetchParams.page,
      id: appOverlapSteamId,
    };
    if (appOverlapSteamId !== '') {
      try {
        setTableData([]);
        setTableTotal(0);
        setFetchParams(params);
        const res = await fetchOverlapSteamGamesRanks({
          ...params,
        });
        const { data = {} as any } = res || {};
        const { total = 0, rank_list = [] } = data;
        setTableTotal(total || rank_list?.length);
        setTableData(rank_list || []);
      } catch (e) {
        console.error(e);
        setTableData([]);
        setTableTotal(0);
      } finally {
        setTableLoading(false);
      }
    } else {
      setTableData([]);
      setTableTotal(0);
      setTableLoading(false);
    }
  };

  const getSummaryData = async (summaryParamsValue: any, segmentKey?: string) => {
    setSummaryLoading(true);

    // 使用 segment key 获取对应的 summary metrics，如 played_play -> ['p&p overlap affinity']
    const summaryMetricKey = segmentKey || summaryParamsValue.page_name;
    const params = {
      ...summaryParamsValue,
      metrics: OVERLAP_STEAM_SUMMARY_METRIC[summaryMetricKey] || OVERLAP_STEAM_SUMMARY_METRIC[summaryParamsValue.page_name],
      id: appOverlapSteamId,
      page: 1,
      page_size: 5,
    };
    delete params.order;
    delete params.order_metric;

    if (params?.id) {
      try {
        if (appOverlapSteamId !== '') {
          const res = await getSteamOverlapGameSummaryDatas({
            ...params,
          });
          const { data = [] } = res || {};
          setSummaryData(data || []);
        }
      } catch (e) {
        console.error(e);
        setSummaryData([]);
      } finally {
        setSummaryLoading(false);
      }
    } else {
      setSummaryData([]);
      setSummaryLoading(false);
    }
  };

  const onTableSortChage = (e: SortByItemType) => {
    const newFetchParams = {
      ...fetchParams,
      order: e?.sortVal,
      order_metric: e?.sortMetric,
      order_type: e?.orderType,
      ratio_type: e?.ratioType ?? 0,
      page: 1,
      // page_size: 100,
      page_size: 50,
    };
    setTableLoading(true);
    getGameData(newFetchParams);
  };

  const onTablePagingChange = (e: PagingQuery) => {
    const newFetchParams = {
      ...fetchParams,
      page: e?.pageIndex || 1,
      // page_size: e?.pageSize || 100,
      page_size: e?.pageSize || 50,
    };
    setTableLoading(true);
    getGameData(newFetchParams);
  };

  const getAmpereRequestParams = useCallback((
    values: Record<string, any> = filterValue,
    pagingParams = otherVendorTableParams,
    includeDate = ampereDateInitializedRef.current,
  ) => {
    const commonParams = handleFilterToApiParams(fetchParams, values);
    const dateRange = values?.[filterKeyMap.RANGE_TIME]?.value || [];
    const activeUsersRange = values?.[OTHER_VENDOR_ACTIVE_USERS_RANGE_KEY]?.value || {};
    const platformParams = getAmperePlatformParams(
      values?.[filterKeyMap.PLATFORMCASCADER]?.value || {},
    );
    const normalizeRangeValue = (rangeValue: any) => (
      rangeValue === '' || rangeValue == null ? undefined : Number(rangeValue)
    );
    const hasInitializedDate = includeDate && dateRange?.[0] && dateRange?.[1];
    return {
      combined_id: gameId,
      start_date: hasInitializedDate ? dateRange[0].format('YYYY-MM-01') : '',
      end_date: hasInitializedDate ? dateRange[1].format('YYYY-MM-01') : '',
      ...platformParams,
      region_type: commonParams?.region_type || 'region',
      region: commonParams?.region || 'global',
      steam_tag: commonParams?.steam_tag || [],
      steam_tag_relation: commonParams?.steam_tag_relation || 'and',
      iegg_genre_req: commonParams?.iegg_genre_req || [],
      active_users_range: {
        min: normalizeRangeValue(activeUsersRange?.min),
        max: normalizeRangeValue(activeUsersRange?.max),
      },
      order: pagingParams.order || 'desc',
      page: pagingParams.page || 1,
      page_size: pagingParams.page_size || 50,
    };
  }, [fetchParams, filterValue, gameId, otherVendorTableParams]);

  const getOtherVendorGameData = async (
    values: Record<string, any> = filterValue,
    pagingParams = otherVendorTableParams,
    includeDate = ampereDateInitializedRef.current,
  ) => {
    if (!gameId) return;
    const requestId = ampereRequestIdRef.current + 1;
    ampereRequestIdRef.current = requestId;
    const requestParams = getAmpereRequestParams(values, pagingParams, includeDate);
    setOtherVendorTableLoading(true);
    setOtherVendorTableData([]);
    setOtherVendorTableTotal(0);
    console.debug('[Other Vendor Overlap] request params:', requestParams);
    try {
      // 先让表格完成一帧 Loading 渲染，再发起请求，避免空态先于 Loading 闪现。
      await new Promise<void>((resolve) => {
        window.requestAnimationFrame(() => resolve());
      });
      if (requestId !== ampereRequestIdRef.current) return;
      const res = await fetchAmpereOverlapGamesRanks(requestParams);
      if (requestId !== ampereRequestIdRef.current) return;
      const responseData = res?.data || {};
      const rankList = responseData?.rank_list || [];
      setOtherVendorTableData(rankList);
      setOtherVendorTableTotal(responseData?.total || 0);

      const responseDateRange = responseData?.date_range || [];
      const latestDate = responseData?.latest_date
        || responseData?.date
        || responseData?.max_date;
      const responseStartDate = responseData?.start_date
        || responseData?.latest_start_date
        || responseDateRange?.[0]
        || latestDate;
      const responseEndDate = responseData?.end_date
        || responseData?.latest_end_date
        || responseDateRange?.[1]
        || latestDate;
      if (responseStartDate && responseEndDate) {
        ampereDateInitializedRef.current = true;
        setFilterValue((currentValue: Record<string, any>) => ({
          ...currentValue,
          [filterKeyMap.RANGE_TIME]: {
            ...currentValue?.[filterKeyMap.RANGE_TIME],
            type: 'month',
            value: [moment(responseStartDate), moment(responseEndDate)],
          },
        }));
      }
    } catch (error) {
      if (requestId !== ampereRequestIdRef.current) return;
      setOtherVendorTableData([]);
      setOtherVendorTableTotal(0);
      console.debug('[Other Vendor Overlap] request error:', error);
    } finally {
      if (requestId === ampereRequestIdRef.current) {
        setOtherVendorTableLoading(false);
      }
    }
  };

  const onOtherVendorTableSortChange = (e: SortByItemType) => {
    const nextParams = {
      ...otherVendorTableParams,
      page: 1,
      order: e?.sortVal,
      order_metric: e?.sortMetric || 'active_users',
    };
    setOtherVendorTableParams(nextParams);
    getOtherVendorGameData(filterValue, nextParams);
  };

  const onOtherVendorTablePagingChange = (e: PagingQuery) => {
    const nextParams = {
      ...otherVendorTableParams,
      page: e?.pageIndex || 1,
      page_size: e?.pageSize || 50,
    };
    setOtherVendorTableParams(nextParams);
    getOtherVendorGameData(filterValue, nextParams);
  };

  // 筛选器防频繁切换：当接口请求进行中时禁用筛选器交互
  const isFilterDisabled = overlapSource === 'otherVendor'
    ? otherVendorTableLoading
    : tableLoading || summaryLoading;

  const handleConfirm = (value: any) => {
    // 防止在数据加载期间触发新的请求
    if (isFilterDisabled) return;
    // Other Vendor 无按钮筛选由 handleChangeExtra 负责，避免选项修改过程中误走 Steam 请求。
    if (overlapSource === 'otherVendor') return;

    // 选中国家地区后，要根据国家获取地区指定，当前国家或地区有数据的 levelA、levelB

    const paramsTemp = handleFilterToApiParams(fetchParams, value);

    const { region_type: currentRegionType, region, page_name: pageName } = paramsTemp;

    // 获取当前选中的 4-segment key
    const segmentKey = value?.overlapSteam_players?.value || 'played_play';

    let engagementLevelA = paramsTemp.engagement_level_a;
    let engagementLevelB = paramsTemp.engagement_level_b;

    // 如果是国家筛选器变动了，指定对应默认有数据的 level，否则不处理
    if (isChangeRegionCountrySelectorRef.current) {
      const level = currentValidEngagementLevel({
        currentRegionType,
        region,
        pageName,
        regionsLevelMapRef,
        marketsLevelMapRef,
      });

      if (!hasUserChangedEngagementTwoRef.current && pageName === 'played') {
        engagementLevelA = level.engagementLevelA;
        engagementLevelB = level.engagementLevelB;

        setFilterValue((prev: Record<string, any>) => {
          return {
            ...prev,
            // 更新 levelA、levelB 筛选器选项值
            [filterKeyMap.ENGAGELEVEL_SEGEMENT_TWO]: {
              value: { engagementLevelA, engagementLevelB },
            },
          };
        });
      }

      if (!hasUserChangedEngagementRef.current && pageName === 'wishlisted') {
        engagementLevelB = level.engagementLevelB;

        setFilterValue((prev: Record<string, any>) => {
          return {
            ...prev,
            // 更新 levelA、levelB 筛选器选项值
            [filterKeyMap.ENGAGELEVEL_SEGEMENT]: {
              value: engagementLevelB,
            },
          };
        });
      }

      isChangeRegionCountrySelectorRef.current = false;
    }

    let currentValidRegion = region;
    if (currentRegionType === RegionType.country) {
      currentValidRegion = getCurrentValidMarket(
        marketsListMapRef.current?.[pageName as OVERLAP_PAGE_TYPE],
        region,
      );
    }

    const newFormValue = {
      ...fetchParams,
      ...paramsTemp,
      order_metric: OVERLAP_STEAM_ORDERMETRIC[segmentKey],
      metrics: OVERLAP_STEAM_METRICPARAMS[segmentKey],

      engagement_level_a: engagementLevelA,
      engagement_level_b: engagementLevelB,

      region: currentValidRegion,

      order: SortType.Desc,
      page: 1,
      page_size: 50,
    };

    setMetricsValue(segmentKey);
    // 请求接口
    setTableLoading(true);
    getGameData(newFormValue, false);
    getSummaryData(newFormValue, segmentKey);
  };

  const handleChangeExtra = (key: string, value?: any, newValue?: Record<string, any>) => {
    if (overlapSource === 'otherVendor') {
      // Platform Cascader 勾选过程中只更新临时值，关闭弹窗时再确认并请求。
      if (key === filterKeyMap.PLATFORMCASCADER && !newValue?.isClose) {
        return;
      }
      if (key === filterKeyMap.RANGE_TIME) {
        ampereDateInitializedRef.current = true;
      }
      const nextFilterValue = newValue || {
        ...filterValue,
        [key]: value,
      };
      const nextTableParams = { ...otherVendorTableParams, page: 1 };
      setOtherVendorTableParams(nextTableParams);
      getOtherVendorGameData(
        nextFilterValue,
        nextTableParams,
        ampereDateInitializedRef.current,
      );
    }

    if (key === filterKeyMap.OVERLAPSTEAM_PLAYERS && newValue) {
      const currentSegment = filterValue?.[filterKeyMap.OVERLAPSTEAM_PLAYERS]?.value as OVERLAP_SEGMENT_TYPE;
      const nextSegment = (value?.value || 'played_play') as OVERLAP_SEGMENT_TYPE;
      const currentRevenueGrowth = filterValue?.[filterKeyMap.REVENUE_GROWTH]?.value;
      const currentSegmentDefault = getSegmentRevenueGrowthDefault(currentSegment || 'played_play');
      const isUsingCurrentSegmentDefault =
        !currentRevenueGrowth?.opt
        || currentRevenueGrowth?.value === ''
        || (
          currentRevenueGrowth?.opt === currentSegmentDefault.opt
          && currentRevenueGrowth?.value === currentSegmentDefault.value
        );

      if (isUsingCurrentSegmentDefault) {
        // eslint-disable-next-line no-param-reassign
        newValue[filterKeyMap.REVENUE_GROWTH] = {
          value: getSegmentRevenueGrowthDefault(nextSegment),
        };
      }
    }

    if (['engageLevel_segement_twoA', 'engageLevel_segement_twoB'].includes(key)) {
      hasUserChangedEngagementTwoRef.current = true;
      // updateURLSearchParams({ custom_engagement_two: true });
    }
    if (key === filterKeyMap.ENGAGELEVEL_SEGEMENT) {
      hasUserChangedEngagementRef.current = true;
      // updateURLSearchParams({ custom_engagement: true });
    }
    if (key === filterKeyMap.REGION_COUNTRY_SELECTOR) {
      isChangeRegionCountrySelectorRef.current = true;
    }
  };

  const handleSteamTagClick = (type: string, tag: string) => {
    if (type !== filterKeyMap.STEAM_TAGS || !tag || isFilterDisabled) return;

    if (overlapSource === 'otherVendor') {
      setOtherVendorTableLoading(true);
    }

    const currentSteamTagFilter = filterValue?.[filterKeyMap.STEAM_TAGS] || defaultFilterValue[filterKeyMap.STEAM_TAGS];
    const currentFilterValue = {
      ...filterValue,
      [filterKeyMap.STEAM_TAGS]: {
        ...currentSteamTagFilter,
        value: [tag],
      },
    };

    setFilterValue(currentFilterValue);
    setFilterExtend(currentFilterValue);
    if (overlapSource === 'otherVendor') {
      const nextTableParams = {
        ...otherVendorTableParams,
        page: 1,
      };
      setOtherVendorTableParams(nextTableParams);
      getOtherVendorGameData(currentFilterValue, nextTableParams);
      return;
    }
    handleConfirm(currentFilterValue);
  };

  const handlePageLog = () => {
    buriedPointGame(PAGE_LOG_INFO, gameId || '');
  };

  // 获取国家地区及对应 level 有数据配置
  const getLevelConfig = async () => {
    if (!appOverlapSteamId) {
      return;
    }

    try {
      // 分页面数据处理
      const res = await getSteamOverlapRegionCountryLevelConfig({ id: appOverlapSteamId });

      const playedData = res?.data?.find(
        (item: RegionCountryLevelConfigItem) => item.page_name === 'played',
      );
      const wishlistData = res?.data?.find(
        (item: RegionCountryLevelConfigItem) => item.page_name === 'wishlisted',
      );

      const playedRegions = playedData?.regions;
      const wishlistedRegions = wishlistData?.regions;

      const playedMarkets = playedData?.markets;
      const wishlistedMarkets = wishlistData?.markets;

      regionsLevelMapRef.current = {
        played: getLevelMap(playedRegions, 'region'),
        wishlisted: getLevelMap(wishlistedRegions, 'region'),
      };
      marketsLevelMapRef.current = {
        played: getLevelMap(playedMarkets, 'market'),
        wishlisted: getLevelMap(wishlistedMarkets, 'market'),
      };

      const listMap = { played: playedMarkets, wishlisted: wishlistedMarkets };
      setMarketListMap(listMap);
      marketsListMapRef.current = listMap;
    } catch (error) {
      // console.log('getSteamOverlapRegionCountryLevelConfig error:', error);
    }
  };

  const getPageData = async () => {
    if (!appOverlapSteamId) {
      setTableLoading(false);
      setSummaryLoading(false);
      return;
    }

    // 需要先获取国家地区对应有数据 level 配置
    await getLevelConfig();

    let fetchParamsTemp = fetchParams;

    const { region_type: currentRegionType, region, page_name: pageName } = fetchParams;

    let currentValidRegion = region;
    if (currentRegionType === RegionType.country) {
      currentValidRegion = getCurrentValidMarket(
        marketsListMapRef.current?.[pageName as OVERLAP_PAGE_TYPE],
        region,
      );
    }

    // 默认 segment key
    const defaultSegment: OVERLAP_SEGMENT_TYPE = isRelease ? 'played_play' : 'wishlisted_play';

    fetchParamsTemp = {
      ...fetchParams,
      page_name: isRelease ? 'played' : 'wishlisted',
      metrics: SEGMENT_METRICS_MAP[defaultSegment],
      metric_filter: SEGMENT_METRIC_FILTER_MAP[defaultSegment],
      order_metric: OVERLAP_STEAM_ORDERMETRIC[defaultSegment],

      region: currentValidRegion,
    };

    getGameData(fetchParamsTemp);
    getSummaryData(fetchParamsTemp, defaultSegment);
  };

  useEffect(() => {
    if (overlapAppIdsLoading) return;

    if (!appOverlapSteamId) {
      setTableLoading(false);
      setSummaryLoading(false);
      return;
    }

    getPageData();
  }, [loading, overlapAppIdsLoading, isRelease, appOverlapSteamId]);

  useEffect(() => {
    handlePageLog();
  }, [gameId]);

  useEffect(() => {
    if (overlapSource !== 'otherVendor' || !gameId) return;

    const loadAmpereData = async () => {
      let nextFilterValue = filterValue;
      try {
        const filterRes = await fetchAmpereOverlapGameFilters({ combined_id: gameId });
        const filterData = filterRes?.data || { platform: [], regions: [], markets: [] };
        setAmpereFilterData(filterData);
        const platformValue = (filterData?.platform || []).reduce(
          (result: Record<string, string[]>, platform: any) => ({
            ...result,
            [platform.value]: (platform.children || []).map((device: any) => device.value),
          }),
          {},
        );
        nextFilterValue = {
          ...filterValue,
          [filterKeyMap.PLATFORMCASCADER]: { value: platformValue },
        };
        setFilterValue(nextFilterValue);
      } catch (error) {
        setAmpereFilterData({ platform: [], regions: [], markets: [] });
        console.debug('[Other Vendor Overlap] filters request error:', error);
      }
      getOtherVendorGameData(nextFilterValue, otherVendorTableParams, false);
    };

    loadAmpereData();
  }, [overlapSource, gameId]);

  const summaryListCur = useMemo(() => {
    const metricList = SUMMARY_METRIC_LIST[metricsValue] || [];
    const metricsSummaryList: any = [];
    // const list = [...tableData];
    if (Array.isArray(summaryData) && summaryData?.length > 0) {
      metricList?.forEach((metricSummaryObj: any) => {
        const list = [...summaryData];
        const metricRankObj: any =
          list?.find((metricObj: any) => metricObj?.metric === metricSummaryObj.metricValue) || {};
        const rankList = metricRankObj.rank_list || [];
        if (Array.isArray(rankList) && rankList?.length > 0) {
          // 获取指标前 5 游戏
          // let topFive = [...list.slice(0, 5)];
          let topFiveNames = '';
          let topFiveValues = '';
          if (Array.isArray(rankList) && rankList?.length > 0) {
            // 取 top5 游戏的游戏名称
            topFiveNames = rankList?.map((item: any) => item.name)?.join(', ');
            // 取 top5 游戏的对应指标数值
            topFiveValues = rankList
              ?.map((item: any) => `${formatPercentNumX(item.value, false) || ''}`)
              ?.join(', ');
          }
          const summaryObj = [
            {
              type: 'normal',
              strKey: metricSummaryObj.textFrontKey,
            },
            {
              type: 'black',
              strKey: name,
            },
            {
              type: 'normal',
              strKey: metricSummaryObj.textMiddleKey,
            },
            {
              type: 'yellow',
              strKey: topFiveNames,
            },
            {
              type: 'normal',
              strKey: metricSummaryObj.textAfterKey,
            },
            {
              type: 'yellow',
              strKey: topFiveValues,
            },
            {
              type: 'normal',
              strKey: metricSummaryObj.textEndKey,
            },
          ];
          metricsSummaryList.push(summaryObj);
        }
      });
    }
    return metricsSummaryList;
  }, [summaryData, metricsValue, locale]);

  // 转化下载的参数
  const getDownloadFetchParams = useCallback(() => {
    const paramsTmp: {
      [key: string]: any;
    } = {
      ...fetchParams,
      regionOptions,
      gameName: name,
    };
    return paramsTmp;
  }, [fetchParams, regionOptions]);

  // Other Vendor 尚未触发列表请求时，也要从页面当前筛选值读取国家设置。
  const getOtherVendorDownloadFetchParams = useCallback(() => {
    return {
      ...getAmpereRequestParams(filterValue, otherVendorTableParams),
      overlap_source: 'ampere',
      regionOptions,
      gameName: name,
    };
  }, [filterValue, getAmpereRequestParams, name, otherVendorTableParams, regionOptions]);

  // 4-segment 文件名映射
  const SEGMENT_FILE_NAMES: Record<string, string> = {
    played_play: 'Played&Play',
    played_wishlist: 'Played&Wishlist',
    wishlisted_play: 'Wishlisted&Play',
    wishlisted_wishlist: 'Wishlisted&Wishlist',
  };

  const steamExportTableCom = (
    <ExportTable
      buttonType="icon"
      tabType={'tabType'}
      entityType={entityType}
      buttonClassName={'ns-btn--icon'}
      tableParams={getDownloadFetchParams()}
      businessType={'combine-detail-steamoverlap'}
      isDisableDownload={loading}
      isShowMaxDownloadRowsTips={false}
      downloadModalSize={820}
      isDisableDataAmount={true}
      newStyleFileName={`${basicInfo?.name}${'_Steam Overlap_'}${
        SEGMENT_FILE_NAMES[metricsValue] || 'Played&Play'
      }`}
      pageId={'pcConsoleSteamOverlap'}
      downloadKey={'pcConsoleDetailSteamOverlap'}
      isUseFormDownload={true}
      useWebDownload={true}
      regionOptions={regionOptions}
      downloadLogConfigKey={pcConsoleDetailOverlap?.[entityType]}
    />
  );

  const otherVendorExportTableCom = (
    <ExportTable
      buttonType={'icon'}
      tabType={'tabType'}
      entityType={entityType}
      buttonClassName={'ns-btn--icon'}
      tableParams={getOtherVendorDownloadFetchParams()}
      businessType={'combine-detail-othervendor-overlap'}
      isDisableDownload={loading}
      isShowMaxDownloadRowsTips={false}
      downloadModalSize={820}
      classModalName={'dbi-other-vendor-overlap-download-modal'}
      downloadTitleTips={'dbi_download_tips'}
      isDisableDataAmount={true}
      newStyleFileName={`${basicInfo?.name}_Overlap_Active Users`}
      pageId={'pcConsoleSteamOverlap'}
      downloadKey={'pcConsoleDetailSteamOverlap'}
      isUseFormDownload={true}
      useWebDownload={true}
      hideStickText={false}
      stickText={formatMessage({ id: 'dbi_download_modal_stick_tips' })}
      regionOptions={regionOptions}
      downloadLogConfigKey={pcConsoleDetailOverlap?.[entityType]}
    />
  );

  const overlapSourceSegment = (
    <Segment
      className='segment-light dbi-overlap-source-segment'
      value={overlapSource}
      options={overlapSourceOptions}
      onChange={(value) => {
        if (value === 'otherVendor') {
          setOtherVendorTableLoading(true);
          setOtherVendorTableData([]);
          setOtherVendorTableTotal(0);
        }
        setOverlapSource(value as OverlapSourceType);
      }}
    />
  );

  return (
    <div className="overlap-steam-content dbi-custom-filter-row-margin-bottom20">
      {/* 筛选器 - 数据加载中时禁用交互，防止频繁切换 */}
      <div className={`overlap-steam-filter-wrapper${isFilterDisabled ? ' overlap-steam-filter-disabled' : ''}`}>
        <CommonFilter
          filterConfirm={overlapSource === 'steam' ? handleConfirm : () => {}}
          filterValue={filterValue}
          setFilterValue={setFilterValue}
          filterCfg={filterCfg}
          defaultInputList={
            overlapSource === 'steam' ? currentDefaultInputList : otherVendorDefaultInputList
          }
          onChangeExtra={handleChangeExtra}
          exportTableCom={overlapSource === 'steam' ? steamExportTableCom : otherVendorExportTableCom}
          firstRowLeading={overlapSourceSegment}
        />
      </div>
      {overlapSource === 'steam' && (
        <>
          {/* Summary */}
          <Summary strList={summaryListCur} loading={summaryLoading} />
          {/* 宽表模块 */}
          <Tables
            fetchParams={fetchParams}
            records={tableData}
            loading={tableLoading}
            tableTotal={tableTotal}
            tableSelection={[]}
            tableSelectionAll={[]}
            pageSortAttr={{
              tablePageIndex: fetchParams?.page,
              tablePageSize: fetchParams?.page_size,
              currentSortValue: fetchParams?.order,
              currentSortMetric: fetchParams?.order_metric,
              currentRatioType: 0,
            }}
            onTableSortChage={onTableSortChage}
            onTablePagingChange={onTablePagingChange}
            onTagClick={handleSteamTagClick}
            entityType={entityType}
            genreSource={'iegg'}
            segmentKey={metricsValue}
          />
        </>
      )}
      {overlapSource === 'otherVendor' && (
        <Tables
          sourceType={'otherVendor'}
          fetchParams={fetchParams}
          records={otherVendorTableData}
          loading={otherVendorTableLoading}
          tableTotal={otherVendorTableTotal}
          tableSelection={[]}
          tableSelectionAll={[]}
          pageSortAttr={{
            tablePageIndex: otherVendorTableParams.page,
            tablePageSize: otherVendorTableParams.page_size,
            currentSortValue: otherVendorTableParams.order,
            currentSortMetric: otherVendorTableParams.order_metric,
          }}
          onTableSortChage={onOtherVendorTableSortChange}
          onTablePagingChange={onOtherVendorTablePagingChange}
          entityType={entityType}
          genreSource={'iegg'}
          onTagClick={handleSteamTagClick}
        />
      )}
    </div>
  );
};

export default React.memo(OverlapSteam);
